import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function safeDownloadBase(name: string): string {
  const trimmed = name.trim().slice(0, 80) || "apply-qr";
  return trimmed.replace(/[^a-zA-Z0-9-_]+/g, "-").replace(/^-|-$/g, "") || "apply-qr";
}

export type ApplicationLinkQrSectionProps = {
  url: string;
  /** Used for the downloaded PNG filename (without .png) */
  downloadBaseName: string;
  headline: string;
  subline?: string;
};

export function ApplicationLinkQrSection({
  url,
  downloadBaseName,
  headline,
  subline,
}: ApplicationLinkQrSectionProps) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setDataUrl(null);
    if (!url.trim()) return;

    QRCode.toDataURL(url, {
      width: 240,
      margin: 2,
      errorCorrectionLevel: "M",
    })
      .then((d) => {
        if (!cancelled) setDataUrl(d);
      })
      .catch(() => {
        if (!cancelled) setError("Could not generate QR code");
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  const handleDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${safeDownloadBase(downloadBaseName)}.png`;
    a.rel = "noopener";
    a.click();
    toast.success("QR code downloaded");
  };

  const handlePrint = () => {
    if (!dataUrl) return;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Pop-up blocked. Allow pop-ups for this site to print the QR card.");
      return;
    }
    const title = escapeHtml(headline);
    const sub = subline ? `<p class="sub">${escapeHtml(subline)}</p>` : "";
    w.document.open();
    w.document.write(`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${title}</title>
<style>
  body { font-family: system-ui, -apple-system, Segoe UI, sans-serif; padding: 28px; text-align: center; max-width: 520px; margin: 0 auto; color: #111; }
  h1 { font-size: 1.35rem; font-weight: 600; margin: 0 0 10px; }
  .sub { color: #444; font-size: 0.95rem; margin: 0 0 20px; line-height: 1.4; }
  img { display: block; margin: 0 auto; width: 256px; height: 256px; }
  .url { font-size: 11px; word-break: break-all; margin: 20px 0 0; color: #333; text-align: left; }
  .hint { margin-top: 14px; font-size: 0.88rem; color: #555; }
  @media print {
    body { padding: 16px; }
    .no-print { display: none !important; }
  }
</style></head><body>
  <h1>${title}</h1>
  ${sub}
  <img src="${dataUrl}" alt="Application QR code" width="256" height="256"/>
  <p class="url">${escapeHtml(url)}</p>
  <p class="hint">Scan with a phone camera to open the application.</p>
  <p class="no-print" style="margin-top:20px"><button type="button" style="padding:10px 18px;font-size:14px;cursor:pointer" onclick="window.print()">Print / Save as PDF</button></p>
</body></html>`);
    w.document.close();
    w.focus();
  };

  if (error) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-900">
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-4 pt-2 border-t border-border/60">
      <div className="shrink-0 rounded-lg border bg-white p-3 inline-flex mx-auto sm:mx-0">
        {dataUrl ? (
          <img src={dataUrl} alt="QR code for application link" width={180} height={180} className="block" />
        ) : (
          <div
            className="w-[180px] h-[180px] bg-muted animate-pulse rounded"
            aria-hidden
          />
        )}
      </div>
      <div className="flex-1 space-y-2 text-center sm:text-left">
        <p className="text-sm text-muted-foreground">
          Scan to open the same link. Use download for a PNG file, or print for a handout or window display.
        </p>
        <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!dataUrl}
            onClick={handleDownload}
          >
            <Download className="h-4 w-4 mr-1.5" />
            Download QR (PNG)
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!dataUrl}
            onClick={handlePrint}
          >
            <Printer className="h-4 w-4 mr-1.5" />
            Print QR card
          </Button>
        </div>
      </div>
    </div>
  );
}
